import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Zap, ArrowRight, X } from "lucide-react";

const FEATURE_COPY = {
    ai_resume_optimize:  { title: "Unlock unlimited resume optimization", blurb: "You've used your monthly free resume optimizations. Talent Pro unlocks unlimited runs plus deep AI review." },
    ai_cover_letter:     { title: "Unlock unlimited cover letters", blurb: "You've hit your free cover-letter allowance. Talent Pro lets you tailor as many as you need." },
    ai_interview_turn:   { title: "Unlock unlimited interview practice", blurb: "You've used your free interview practice sessions. Talent Pro gives you unlimited coaching." },
    ai_job_match:        { title: "Unlock unlimited job matches", blurb: "Free plan includes 3 AI job matches per month. Upgrade for unlimited daily matches." },
    ai_salary_insights:  { title: "Salary Insights are a Pro feature", blurb: "See how your target salary compares to peers on Simonara — available on Talent Pro." },
    ai_resume_review:    { title: "AI Resume Review is a Pro feature", blurb: "The deep resume review is included in Talent Pro." },
    active_jobs:         { title: "You're at your active-job limit", blurb: "Upgrade to Starter for 3 active jobs, or Growth for unlimited postings." },
    hiring_briefs:       { title: "You've used your AI Hiring Briefs this month", blurb: "Starter includes 10 briefs/month · Growth is unlimited." },
    team_seats:          { title: "You've filled every team seat on this plan", blurb: "Growth includes 4 seats. Enterprise is custom." },
};

const upgradeHref = (feature, requiredPlan) => {
    if (requiredPlan?.startsWith("employer_")) return "/employer/billing";
    return "/talent/billing";
};

export const UpgradeModalHost = () => {
    const [detail, setDetail] = useState(null);
    useEffect(() => {
        const handler = (e) => setDetail(e.detail);
        window.addEventListener("simonara:upgrade-required", handler);
        return () => window.removeEventListener("simonara:upgrade-required", handler);
    }, []);
    if (!detail) return null;
    const copy = FEATURE_COPY[detail.feature] || {
        title: "Upgrade required",
        blurb: `This feature is available on ${detail.required_plan?.replace(/_/g, " ")}.`,
    };
    const isEmployer = detail.required_plan?.startsWith("employer_");
    return (
        <div
            data-testid="upgrade-modal"
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setDetail(null)}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-md rounded-2xl bg-background border border-border shadow-2xl p-7"
            >
                <button
                    onClick={() => setDetail(null)}
                    className="absolute top-3 right-3 text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted/50"
                    aria-label="Close"
                    data-testid="upgrade-modal-close"
                >
                    <X size={16} />
                </button>
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500 to-emerald-500 text-white flex items-center justify-center mb-3">
                    {isEmployer ? <ShieldCheck size={22} /> : <Zap size={22} />}
                </div>
                <h2 className="font-heading text-xl font-semibold text-foreground">{copy.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{copy.blurb}</p>
                {typeof detail.limit === "number" && detail.limit > 0 && (
                    <div className="mt-3 text-xs text-muted-foreground">
                        Used <span className="font-semibold text-foreground">{detail.used}</span> of {detail.limit} this month
                    </div>
                )}
                <div className="mt-6 flex items-center gap-3">
                    <Link
                        to={upgradeHref(detail.feature, detail.required_plan)}
                        data-testid="upgrade-modal-cta"
                        onClick={() => setDetail(null)}
                        className="btn-primary text-sm inline-flex items-center"
                    >
                        {isEmployer ? "See employer plans" : "Upgrade to Talent Pro"} <ArrowRight size={13} className="ml-1.5" />
                    </Link>
                    <button
                        onClick={() => setDetail(null)}
                        data-testid="upgrade-modal-dismiss"
                        className="text-sm text-muted-foreground hover:text-foreground px-3 py-1.5"
                    >
                        Not now
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpgradeModalHost;
